/** A dense read-only overview of one order + its bookings grid — the Orders grid's
 *  master-detail panel (the full page is the order record, `OrderDetailPage`). Owns
 *  its fetch/loading/error. Every reference carries its quick actions (orderLinks /
 *  bookingLinks). */
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as ordersApi from '@/api/orders';
import { BoolField, Field, FieldGrid, LinkField, SectionCard } from '@/components/ReadView';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { BookingStateChip } from '@/features/bookings/BookingStateChip';
import {
  phoneActions,
  refActions,
  restechAccountActions,
  tenantActions,
} from '@/features/bookings/overview/bookingLinks';
import { formatMoney, Ts } from '@/features/bookings/overview/common';
import { bookingsHref, cond } from '@/utils/gridLinks';

import { OrderBookingsGrid } from './OrderBookingsGrid';
import {
  contactEmailActions,
  orderActiveActions,
  orderCustomIdActions,
  orderIdActions,
  orderIntegrationActions,
  orderRefActions,
  orderStateActions,
  orderTimestampActions,
  orderUserActions,
  type OrderCtx,
} from './orderLinks';

function money(minor: number | null, o: ordersApi.OrderDetail): string {
  return formatMoney(minor, o.currency, o.currency_precision) ?? '—';
}

function contactName(c: ordersApi.OrderContact | null): string {
  if (!c) return '—';
  return `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '—';
}

function userName(u: { first_name: string | null; last_name: string | null; id: number }): string {
  return `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || `#${u.id}`;
}

export function OrderSummary({
  orderId,
  showHeader = true,
}: {
  orderId: number;
  /** Hidden on the detail page (PageHeader shows identity); shown in the grid panel. */
  showHeader?: boolean;
}) {
  const { data: o, isLoading, error } = useQuery({
    queryKey: ordersApi.orderDetailKey(orderId),
    queryFn: () => ordersApi.retrieve(orderId),
    enabled: Number.isFinite(orderId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !o) return <Alert severity="error">Couldn’t load order #{orderId}.</Alert>;

  const c = o.contact;
  const ctx: OrderCtx = { tenantId: o.tenant?.id };
  const bookingsOfOrder = o.order_ref
    ? bookingsHref([cond('order', o.order_ref)], ctx.tenantId ?? null)
    : null;

  return (
    <Box>
      {/* Header */}
      {showHeader && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mr: 1 }}>
            {o.order_ref ? (
              <ValueActions actions={orderRefActions(o.order_ref, ctx)}>{o.order_ref}</ValueActions>
            ) : o.custom_id ? (
              <ValueActions actions={orderCustomIdActions(o.custom_id, ctx)}>{o.custom_id}</ValueActions>
            ) : (
              `Order #${o.id}`
            )}
          </Typography>
          <ValueActions chip actions={orderStateActions(o.booking_state_display, ctx)}>
            <BookingStateChip booking_state={o.booking_state} booking_state_display={o.booking_state_display} />
          </ValueActions>
          <ValueActions chip actions={orderIdActions(o.id)}>
            <Chip size="small" variant="outlined" label={`#${o.id}`} />
          </ValueActions>
          {!o.active && (
            <ValueActions chip actions={orderActiveActions(false, ctx)}>
              <Chip size="small" color="default" label="Inactive" />
            </ValueActions>
          )}
          {o.tenant && (
            <ValueActions actions={tenantActions(o.tenant)}>
              <Link component={RouterLink} to={`/tour-operators/${o.tenant.id}`} underline="hover" variant="body2">
                {o.tenant.name}
              </Link>
            </ValueActions>
          )}
        </Stack>
      )}

      {/* Identity & Status */}
      <SectionCard title="Identity & status">
        <FieldGrid>
          <Field
            label="Order #"
            value={o.order_ref ?? '—'}
            {...(o.order_ref && { actions: orderRefActions(o.order_ref, ctx) })}
          />
          <Field
            label="Custom ID"
            value={o.custom_id ?? '—'}
            {...(o.custom_id && { actions: orderCustomIdActions(o.custom_id, ctx) })}
          />
          <Field
            label="Supplier #"
            value={o.supplier_ref ?? '—'}
            {...(o.supplier_ref && { actions: refActions('supplier', o.supplier_ref, ctx) })}
          />
          <Field
            label="Reseller #"
            value={o.reseller_ref ?? '—'}
            {...(o.reseller_ref && { actions: refActions('reseller', o.reseller_ref, ctx) })}
          />
          <Field
            label="State"
            value={o.booking_state_display}
            {...(o.booking_state_display && { actions: orderStateActions(o.booking_state_display, ctx) })}
          />
          <BoolField label="Active" value={o.active} />
        </FieldGrid>
      </SectionCard>

      {/* Tags — the ORDER's own tags (CP-8095), not its bookings'. The bookings
          grid below shows each booking's tags in its own column. */}
      <SectionCard title="Tags">
        <TagBadgeSet
          tags={o.tags}
          maxVisible={12}
          chipActions={(t) => tagChipActions(t, { tenantId: ctx.tenantId, owner: 'order' })}
        />
      </SectionCard>

      {/* Contact */}
      <SectionCard title="Contact">
        <FieldGrid>
          {/* The ACCOUNT the order belongs to (OrderDetail.user) — carried by the
              payload from the start, never rendered before. Distinct from the
              free-text contact block the booking system sent. */}
          <LinkField
            label="Account"
            to={o.user ? `/users/${o.user.id}` : null}
            text={o.user ? userName(o.user) : '—'}
            {...(o.user && { actions: orderUserActions(o.user, ctx) })}
          />
          <Field label="Name" value={contactName(c)} />
          <Field
            label="Email"
            value={c?.email ? <Link href={`mailto:${c.email}`} underline="hover">{c.email}</Link> : '—'}
            {...(c?.email && { actions: contactEmailActions(c.email, ctx) })}
          />
          <Field
            label="Phone"
            value={c?.phone ? <Link href={`tel:${c.phone}`} underline="hover">{c.phone}</Link> : '—'}
            {...(c?.phone && { actions: phoneActions(c.phone, ctx) })}
          />
          <Field label="Company" value={c?.company ?? '—'} />
          <Field label="Country" value={c?.country ?? '—'} />
        </FieldGrid>
      </SectionCard>

      {/* Financials */}
      <SectionCard title="Financials">
        <FieldGrid>
          <Field label="Currency" value={o.currency ?? '—'} />
          <Field label="Net" value={money(o.price_net, o)} />
          <Field label="Original" value={money(o.price_original, o)} />
          <Field label="Retail" value={money(o.price_retail, o)} />
          <Field label="Tax" value={money(o.tax_amount, o)} />
        </FieldGrid>
      </SectionCard>

      {/* Provenance */}
      <SectionCard title="Provenance">
        <FieldGrid>
          <Field
            label="Integration"
            value={o.integration ?? '—'}
            {...(o.integration && { actions: orderIntegrationActions(o.integration, ctx) })}
          />
          <Field
            label="Restech account"
            value={o.restech_account ?? '—'}
            {...(o.restech_account && { actions: restechAccountActions(o.restech_account, ctx) })}
          />
          <Field
            label="Created"
            value={<Ts value={o.created_ts} />}
            {...(o.created_ts && { actions: orderTimestampActions('created', o.created_ts, ctx) })}
          />
          <Field
            label="Updated"
            value={<Ts value={o.updated_ts} />}
            {...(o.updated_ts && { actions: orderTimestampActions('updated', o.updated_ts, ctx) })}
          />
          <Field label="Remote created" value={<Ts value={o.remote_created_ts} />} />
          <Field label="Remote updated" value={<Ts value={o.remote_updated_ts} />} />
        </FieldGrid>
      </SectionCard>

      {/* Schedule */}
      <SectionCard title="Schedule">
        <FieldGrid>
          <Field label="Start" value={<Ts value={o.start_datetime} />} />
          <Field label="End" value={<Ts value={o.end_datetime} />} />
          <Field
            label="Bookings"
            value={o.booking_count}
            {...(bookingsOfOrder && { actions: [act.filter(bookingsOfOrder, 'Bookings in this order')] })}
          />
        </FieldGrid>
      </SectionCard>

      {/* Bookings */}
      <SectionCard title={`Bookings (${o.booking_count})`}>
        <OrderBookingsGrid bookings={o.bookings} ctx={ctx} />
      </SectionCard>
    </Box>
  );
}
